/**
 * Geometry helpers for the map.
 *
 * The search API returns `geoPoint` and `geoShape` as GeoJSON geometry
 * objects (previously WKT strings). GeoJSON coordinates are ordered
 * [lng, lat]; Leaflet expects [lat, lng], so these helpers transpose.
 */

function unwrap(value) {
  // React Search UI may surface a field as { raw } — tolerate either shape.
  return value && value.raw ? value.raw : value;
}

function isFinitePair(lng, lat) {
  return (
    typeof lng === 'number' &&
    typeof lat === 'number' &&
    Number.isFinite(lng) &&
    Number.isFinite(lat)
  );
}

// GeoJSON position [lng, lat] -> Leaflet [lat, lng]; null if malformed.
function positionToLatLng(position) {
  if (!Array.isArray(position)) return null;
  const [lng, lat] = position;
  if (!isFinitePair(lng, lat)) return null;
  return [lat, lng];
}

// Map a ring/line of GeoJSON positions to Leaflet latlngs; null if any invalid.
function ringToLatLngs(ring) {
  if (!Array.isArray(ring) || ring.length === 0) return null;
  const latLngs = ring.map(positionToLatLng);
  return latLngs.some(p => p === null) ? null : latLngs;
}

/**
 * Convert a GeoJSON Point geometry into a Leaflet [lat, lng] coordinate.
 *
 * @param {{ type: string, coordinates: number[] } | { raw: object }} geometry
 * @returns {[number, number] | null}
 */
export function parseGeoPoint(geometry) {
  const geom = unwrap(geometry);
  if (!geom || geom.type !== 'Point') return null;
  return positionToLatLng(geom.coordinates);
}

/**
 * Convert a GeoJSON geometry into Leaflet latlngs suitable for L.polygon.
 *
 * Returns null for Point geometries (those render as markers via
 * parseGeoPoint). Handles Polygon and MultiPolygon — the shapes the API
 * uses for areas such as scheduled monuments and parks.
 *
 * @param {{ type: string, coordinates: any[] } | { raw: object }} geometry
 * @returns {Array | null} Leaflet latlng structure, or null if not an area.
 */
export function parseGeoShape(geometry) {
  const geom = unwrap(geometry);
  if (!geom || !Array.isArray(geom.coordinates)) return null;

  switch (geom.type) {
    case 'Polygon': {
      // coordinates: [outerRing, ...holes] -> Leaflet [[latlng...], ...]
      const rings = geom.coordinates.map(ringToLatLngs);
      return rings.some(r => r === null) ? null : rings;
    }
    case 'MultiPolygon': {
      // coordinates: [[outerRing, ...holes], ...] -> Leaflet 3-deep latlngs
      const polygons = geom.coordinates.map(polygon => {
        const rings = polygon.map(ringToLatLngs);
        return rings.some(r => r === null) ? null : rings;
      });
      return polygons.some(p => p === null) ? null : polygons;
    }
    default:
      // Point (and any unsupported geometry) is not an area shape.
      return null;
  }
}
